import {ChangeEvent} from 'react';
import {DraftDetails} from './api';

export type AttorneyIntakeFieldName = Exclude<keyof DraftDetails, 'address_source' | 'verification_status' | 'version' | 'updated_at'>;
export type AttorneyIntakeSelectOption = {value: string; label: string};

type FieldDefinition = {
  name: AttorneyIntakeFieldName;
  label: string;
  type: 'text' | 'date';
  required: boolean;
  autoComplete: string;
};

export const attorneyIntakeFields: readonly FieldDefinition[] = [
  {name: 'documents_to_serve', label: 'Documents to serve (one exact title per line)', type: 'text', required: false, autoComplete: 'off'},
  {name: 'client_reference', label: 'Client reference', type: 'text', required: false, autoComplete: 'off'},
  {name: 'case_caption', label: 'Case caption', type: 'text', required: true, autoComplete: 'off'},
  {name: 'court_reference', label: 'Court or jurisdiction (optional)', type: 'text', required: false, autoComplete: 'off'},
  {name: 'case_number', label: 'Case number (optional)', type: 'text', required: false, autoComplete: 'off'},
  {name: 'service_type_code', label: 'Service type code', type: 'text', required: true, autoComplete: 'off'},
  {name: 'deadline_date', label: 'Deadline date', type: 'date', required: true, autoComplete: 'off'},
  {name: 'recipient_name', label: 'Recipient name', type: 'text', required: true, autoComplete: 'name'},
  {name: 'recipient_type_code', label: 'Recipient type code', type: 'text', required: true, autoComplete: 'off'},
  {name: 'address_line_1', label: 'Address line 1', type: 'text', required: true, autoComplete: 'address-line1'},
  {name: 'address_line_2', label: 'Address line 2 (optional)', type: 'text', required: false, autoComplete: 'address-line2'},
  {name: 'city', label: 'City', type: 'text', required: true, autoComplete: 'address-level2'},
  {name: 'admin_area', label: 'State or administrative area', type: 'text', required: true, autoComplete: 'address-level1'},
  {name: 'postal_code', label: 'Postal code', type: 'text', required: true, autoComplete: 'postal-code'},
  {name: 'country_code', label: 'Country code', type: 'text', required: true, autoComplete: 'country'},
] as const;

type Props = {
  names?: readonly AttorneyIntakeFieldName[];
  values: Partial<Record<AttorneyIntakeFieldName, string | null>>;
  errors?: Record<string, string>;
  idPrefix?: string;
  fieldClassName?: string;
  labels?: Partial<Record<AttorneyIntakeFieldName, string>>;
  selectOptions?: Partial<Record<AttorneyIntakeFieldName, readonly AttorneyIntakeSelectOption[]>>;
  onChange?: (name: AttorneyIntakeFieldName, value: string) => void;
};

export function AttorneyIntakeFields({names, values, errors = {}, idPrefix = 'intake', fieldClassName = 'field', labels = {}, selectOptions = {}, onChange}: Props) {
  const selected = names ? attorneyIntakeFields.filter((field) => names.includes(field.name)) : attorneyIntakeFields;

  return <>{selected.map((field) => {
    const id = `${idPrefix}-${field.name}`;
    const errorId = `${id}-error`;
    const label = labels[field.name] ?? field.label;
    const options = selectOptions[field.name];
    const value = String(values[field.name] ?? (field.name === 'country_code' ? 'US' : ''));
    const valueProps = onChange
      ? {value, onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => onChange(field.name, event.target.value)}
      : {defaultValue: value};

    return <div className={fieldClassName} key={field.name}>
      <label htmlFor={id} data-required={field.required || undefined}>{label}</label>
      {field.name==='documents_to_serve'?<textarea {...valueProps} id={id} name={field.name} rows={5} maxLength={50200} aria-invalid={errors[field.name]?'true':undefined} aria-describedby={errors[field.name]?errorId:undefined}/>:options ? <select
        {...valueProps}
        id={id}
        name={field.name}
        required={field.required}
        autoComplete={field.autoComplete}
        aria-invalid={errors[field.name] ? 'true' : undefined}
        aria-describedby={errors[field.name] ? errorId : undefined}
      >
        <option value="" disabled>Select {label.toLocaleLowerCase()}</option>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select> : <input
        {...valueProps}
        id={id}
        name={field.name}
        type={field.type}
        required={field.required}
        autoComplete={field.autoComplete}
        aria-invalid={errors[field.name] ? 'true' : undefined}
        aria-describedby={errors[field.name] ? errorId : undefined}
      />}
      {errors[field.name] && <span id={errorId} className="field-error">{errors[field.name]}</span>}
    </div>;
  })}</>;
}
